// caiet UC91-14 / ACCS-09 — the lot filter of the unaggregated view. The lot is
// chosen from the lots the role reaches, not typed: a typed lot outside the scope
// filtered to nothing and said nothing. A Registrar holds hundreds of lots, so the
// list is searched and paged on the server (useStockLots) and only a page is ever
// rendered. MUD has no combobox (wireframes/00-decisions.md D4): this is AGE's
// input over a listbox, with the keyboard and ARIA of useComboboxA11y, the
// composition MedicineMultiSelect already is. The list sits in the flow of the
// filter panel rather than floating: the panel is already an overlay, and its
// height cap would clip a second one inside it.
import { useEffect, useRef, useState, type ElementRef } from 'react';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudChip, MudTextInput } from '@egov-moldova/mud-react';
import { useComboboxA11y } from '@simsm/ui-shared';
import { useStockLots } from './useStockLots';

interface LotFilterFieldProps {
  selected: string[];
  registrarIds: string[];
  siteIds: string[];
  onChange: (next: string[]) => void;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

export function LotFilterField({
  selected,
  registrarIds,
  siteIds,
  onChange,
}: LotFilterFieldProps): JSX.Element {
  const { t } = useTranslate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  // Escape hides the list without discarding the query, as in MedicineMultiSelect.
  const [dismissed, setDismissed] = useState(false);
  const debounced = useDebounced(query.trim(), 250);
  const rootRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<ElementRef<typeof MudTextInput>>(null);
  const listboxRef = useRef<HTMLDivElement>(null);
  const panelOpen = open && !dismissed;

  const lots = useStockLots({ query: debounced, registrarIds, siteIds, enabled: open });
  const options = (lots.data?.pages ?? [])
    .flatMap((page) => page.lots)
    .filter((lot) => !selected.includes(lot));

  useEffect(() => {
    function onPointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  // A pick keeps the list open: several lots are a normal filter, and the chosen
  // one leaves the list, so the next is one keystroke away.
  function pick(lot: string): void {
    onChange([...selected, lot]);
  }

  const combobox = useComboboxA11y({
    open: panelOpen && options.length > 0,
    optionCount: options.length,
    fieldRef,
    listboxRef,
    onSelect: (index) => {
      const lot = options[index];
      if (lot !== undefined) pick(lot);
    },
    onOpen: () => {
      setOpen(true);
      setDismissed(false);
    },
    onClose: () => setDismissed(true),
    onTypeahead: (character) => {
      setQuery(query + character);
      setOpen(true);
      setDismissed(false);
    },
  });

  return (
    <div
      ref={rootRef}
      className="simsm-filter-bar__field simsm-lot-filter"
      // keydown is composed, so the field's keys reach this wrapper across
      // mud-text-input's shadow boundary.
      onKeyDown={combobox.onFieldKeyDown}
    >
      <MudTextInput
        ref={fieldRef}
        size="md"
        label={t('stockChart.filters.lot')}
        value={query}
        placeholder={t('stockChart.filters.lot_placeholder')}
        onMudInput={(e: CustomEvent<{ value: string }>) => {
          setQuery(e.detail.value);
          setOpen(true);
          setDismissed(false);
        }}
        onMudFocus={() => {
          if (!combobox.shouldOpenOnFieldFocus()) return;
          setOpen(true);
          setDismissed(false);
        }}
      />

      {panelOpen ? (
        <div className="simsm-lot-filter__panel">
          {lots.isFetching && !lots.isFetchingNextPage ? (
            <p className="simsm-lot-filter__status" role="status">
              {t('stockChart.filters.lot_loading')}
            </p>
          ) : null}
          {!lots.isFetching && lots.isError ? (
            // A list that failed to load is not a list with no lots.
            <p className="simsm-lot-filter__status" role="status">
              {t('common.load_error')}
            </p>
          ) : null}
          {!lots.isFetching && !lots.isError && options.length === 0 ? (
            <p className="simsm-lot-filter__status" role="status">
              {t('stockChart.filters.lot_no_results')}
            </p>
          ) : null}
          {/* The listbox exists only while it holds options: an empty one is a
              role whose required children are missing. */}
          {options.length > 0 ? (
            <div
              ref={listboxRef}
              id={combobox.listboxId}
              role="listbox"
              // Named and in the tab order: a scroll container whose options all
              // carry tabindex="-1" is otherwise unreachable by keyboard in Safari.
              tabIndex={0}
              aria-label={t('stockChart.filters.lot')}
              className="simsm-lot-filter__list"
              onKeyDown={combobox.onListboxKeyDown}
            >
              {options.map((lot, index) => (
                <div
                  key={lot}
                  id={combobox.optionId(index)}
                  role="option"
                  tabIndex={-1}
                  aria-selected={combobox.activeIndex === index}
                  className="simsm-lot-filter__option"
                  onFocus={() => combobox.onOptionFocus(index)}
                  onClick={() => combobox.selectOption(index)}
                >
                  {lot}
                </div>
              ))}
            </div>
          ) : null}
          {lots.hasNextPage ? (
            <MudButton
              shape="circular"
              variant="strict"
              appearance="text"
              size="sm"
              className="simsm-lot-filter__more"
              disabled={lots.isFetchingNextPage}
              onClick={() => void lots.fetchNextPage()}
            >
              {lots.isFetchingNextPage
                ? t('stockChart.filters.lot_loading')
                : t('stockChart.filters.lot_more')}
            </MudButton>
          ) : null}
        </div>
      ) : null}

      {/* MUD adds the verb to the chip's ×: it reads "Elimină <lot>" once, in the page language. */}
      {selected.length > 0 ? (
        <ul className="simsm-medmulti__chips">
          {selected.map((lot) => (
            <li key={lot}>
              <MudChip
                type="input"
                size="sm"
                removable
                onMudRemove={() => onChange(selected.filter((item) => item !== lot))}
              >
                {lot}
              </MudChip>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
